import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Megaphone, DollarSign, Users, MapPin, Calendar, Loader2, Sparkles, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PromoPartnersDirectory() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { token } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const [accepting, setAccepting] = useState(null);
  // Avoid double-firing the post-login intent if a render race happens.
  const intentExecutedRef = useRef(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [evRes, meRes] = await Promise.all([
        axios.get(`${API}/promo/events`),
        token ? axios.get(`${API}/street-team/me`, { headers: { Authorization: `Bearer ${token}` } }) : Promise.resolve({ data: null }),
      ]);
      setEvents(evRes.data.events || []);
      setMe(meRes.data);
      return meRes.data;
    } catch (e) { /* swallow */ return null; }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-line */ }, [token]);

  // ------------------------------------------------------------------
  // Internal opt-in helper that returns its updated `me` state so callers
  // (e.g. accept) can immediately continue without waiting for another
  // React render cycle. Resolves the "page just refreshed after Accept"
  // bug reported in production.
  // ------------------------------------------------------------------
  const doOptIn = async ({ silent = false } = {}) => {
    if (!token) return null;
    try {
      const res = await axios.post(`${API}/street-team/opt-in`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!silent) toast.success('Welcome to the street team!');
      // Re-fetch /me so the rest of the page reflects the new state.
      const updated = await fetchAll();
      // Fallback: trust the opt-in response if /me lookup didn't return one.
      return updated || res.data || { enabled: true };
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to join');
      return null;
    }
  };

  const doAccept = async (eventId, { silent = false } = {}) => {
    if (!token) return false;
    setAccepting(eventId);
    try {
      await axios.post(`${API}/promo/events/${eventId}/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!silent) toast.success('Campaign accepted!');
      navigate('/dashboard/street-team');
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to accept campaign');
      return false;
    } finally {
      setAccepting(null);
    }
  };

  // Public buttons -----------------------------------------------------
  const optIn = async () => {
    if (!token) {
      navigate('/login?returnTo=' + encodeURIComponent('/promo-partners?intent=join'));
      return;
    }
    await doOptIn();
  };

  const accept = async (eventId) => {
    if (!token) {
      navigate('/login?returnTo=' + encodeURIComponent(`/promo-partners?intent=accept&event_id=${eventId}`));
      return;
    }
    // Already-enabled fast path — straight to the accept call.
    if (me?.enabled) {
      await doAccept(eventId);
      return;
    }
    // New user: join AND accept in one click instead of dumping them
    // back on the page (the old code did `await optIn(); return;`,
    // which left users staring at a refreshed page).
    const updated = await doOptIn({ silent: true });
    if (updated?.enabled) {
      toast.success('Joined the street team — accepting campaign…');
      await doAccept(eventId, { silent: false });
    }
  };

  // ----------------------------------------------------------------
  // Post-login intent runner — when a logged-out user clicked Join /
  // Accept, we bounced them through /login with the intent embedded
  // in the returnTo URL. After they return here logged in, we read
  // `?intent=...` and finish what they originally tried to do.
  // ----------------------------------------------------------------
  useEffect(() => {
    if (intentExecutedRef.current) return;
    if (loading || !token) return; // wait until /me + events are loaded
    const intent = searchParams.get('intent');
    if (!intent) return;
    intentExecutedRef.current = true;
    const eventId = searchParams.get('event_id');
    // Clear the query so a manual refresh doesn't re-trigger.
    const clean = new URLSearchParams(searchParams);
    clean.delete('intent');
    clean.delete('event_id');
    setSearchParams(clean, { replace: true });
    (async () => {
      if (intent === 'join') {
        if (!me?.enabled) await doOptIn();
        return;
      }
      if (intent === 'accept' && eventId) {
        if (!me?.enabled) {
          const updated = await doOptIn({ silent: true });
          if (!updated?.enabled) return;
        }
        await doAccept(eventId);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, token, me]);

  const formatCommission = (ev) => {
    const flat = (ev.commission_flat_cents || 0) / 100;
    const pct = ev.commission_percent || 0;
    const parts = [];
    if (flat) parts.push(`$${flat.toFixed(2)}/ticket`);
    if (pct) parts.push(`${pct}% of sale`);
    return parts.join(' + ') || 'Commission set per event';
  };

  return (
    <div className="min-h-screen bg-background pt-20 pb-20" data-testid="promo-directory">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-500 text-xs font-semibold mb-4">
            <Megaphone className="h-3 w-3" /> Eventism Street Team
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-3">Make money promoting events you love</h1>
          <p className="text-base text-muted-foreground max-w-2xl mx-auto">
            Pick a campaign, share your tracking link, and earn instant commission on every ticket you sell. No quotas, no risk — just your network and a payout.
          </p>
          {!me?.enabled && (
            <Button size="lg" onClick={optIn} className="mt-6 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="opt-in-btn">
              <Sparkles className="h-4 w-4 mr-2" /> Join the Street Team
            </Button>
          )}
          {me?.enabled && (
            <div className="mt-6 inline-flex items-center gap-2 text-sm">
              <Badge className="bg-emerald-600 text-white"><Check className="h-3 w-3 mr-1" /> Active street-team member</Badge>
              <Link to="/dashboard/street-team" className="text-indigo-400 hover:underline">Open my dashboard →</Link>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-indigo-500" /></div>
        ) : events.length === 0 ? (
          <Card className="text-center py-16">
            <CardContent>
              <Megaphone className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-sm font-medium">No active campaigns right now</p>
              <p className="text-xs text-muted-foreground mt-1">Check back soon — or join the team to get email alerts.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {events.map(ev => (
              <Card key={ev.event_id} className="border-border overflow-hidden hover:border-emerald-500/40 transition-colors" data-testid={`promo-event-${ev.event_id}`}>
                {ev.image_url && (
                  <div className="aspect-video overflow-hidden">
                    <img src={ev.image_url} alt={ev.name} className="w-full h-full object-cover" />
                  </div>
                )}
                <CardContent className="p-4 space-y-2">
                  <h3 className="font-semibold">{ev.name}</h3>
                  {ev.tagline && <p className="text-xs text-muted-foreground line-clamp-2">{ev.tagline}</p>}
                  <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                    {ev.city && <Badge variant="outline" className="text-[10px] capitalize"><MapPin className="h-2.5 w-2.5 mr-0.5" />{(ev.city || '').replace(/_/g, ' ')}</Badge>}
                    {ev.event_date && <Badge variant="outline" className="text-[10px]"><Calendar className="h-2.5 w-2.5 mr-0.5" />{new Date(ev.event_date).toLocaleDateString()}</Badge>}
                  </div>
                  <div className="rounded-md bg-emerald-500/10 border border-emerald-500/20 p-2 flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                    <p className="text-xs font-semibold">{formatCommission(ev)}</p>
                  </div>
                  {ev.marketing_directive && <p className="text-[11px] text-muted-foreground italic line-clamp-3">"{ev.marketing_directive}"</p>}
                  <Button onClick={() => accept(ev.event_id)} disabled={accepting === ev.event_id} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" size="sm" data-testid={`accept-${ev.event_id}`}>
                    {accepting === ev.event_id ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Accept Campaign →'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
